import React, { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import {
	Typography,
	Card,
	CardContent,
	IconButton,
	CardActions,
	Box,
	Modal,
	CircularProgress,
} from '@mui/material';
import { filesize } from 'filesize';
import styles from './AttachmentCard.module.css';
import { CheckIcon, ClearIcon } from 'components/icons';
import { useNotificationProvider } from 'hooks';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

type AttachmentSource = 'vv' | 'dds' | 'cancam' | 'bucket';

interface AttachmentCardProps {
	index: number;
	id: number | string; // string for CANcam VideoID (UUID)
	name: string;
	size: number;
	onConfirmDelete: (id: number | string) => void;
	contentType: string;
	isDDSFile?: boolean; // Legacy prop - use source instead
	source?: AttachmentSource; // New prop to identify attachment source
	disableDelete?: boolean;
}

const AttachmentCard: React.FC<AttachmentCardProps> = (props) => {
	const {
		index,
		id,
		name,
		size,
		onConfirmDelete,
		contentType,
		isDDSFile,
		source,
		disableDelete,
	} = props;

	// Determine effective source from props
	const effectiveSource: AttachmentSource = source || (isDDSFile ? 'dds' : 'vv');

	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const [openAttachment, setOpenAttachment] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<boolean>(false);
	const { notifyError } = useNotificationProvider();

	const handleAttachmentClick = () => {
		if (
			contentType &&
			(contentType.includes('image') || contentType.includes('video'))
		) {
			setError(false);
			setLoading(true);
			setOpenAttachment(true);
		} else {
			notifyError('No preview available for this file type.');
		}
	};

	const handleCloseModal = () => {
		setOpenAttachment(false);
	};

	const handleLoad = () => {
		setLoading(false); // Set loading to false when the image or video is loaded
	};

	const handleError = () => {
		setLoading(false); // Set loading to false if there is an error loading the image or video
		setError(true);
	};

	// Generate preview URL based on source
	const getPreviewUrl = (): string => {
		switch (effectiveSource) {
			case 'cancam':
				return `/api3.2/cancam/videos/${id}/stream`;
			case 'dds':
				return `/api2.0/CANDDS/Preview/${id}`;
			case 'bucket':
				return `/api3.2/noauth/bucket/${id}`;
			case 'vv':
			default:
				return `/api/fileuploadservice/Preview?FileId=${id}`;
		}
	};

	const previewUrl = getPreviewUrl();
	const isCancam = effectiveSource === 'cancam';

	return (
		<Card className={styles.card}>
			<CardContent
				className={styles.cardContent}
				onClick={handleAttachmentClick}
			>
				{showConfirmDelete ? (
					<Box className={styles.confirmDeleteContainer}>
						<Typography>
							Are you sure you want to delete this file?
						</Typography>
					</Box>
				) : (
					<>
						<Box className={styles.iconContainer}>
							{contentType && contentType.includes('image') ? (
								<img
									src={previewUrl}
									alt=""
									className={styles.imagePreview}
								/>
							) : contentType && contentType.includes('video') ? (
								isCancam ? (
									<PhotoCameraIcon
										className={styles.imagePreview}
										sx={{ color: 'info.main' }}
									/>
								) : (
									<VideoCameraBackIcon
										className={styles.imagePreview}
									/>
								)
							) : (
								<FilePresentIcon
									className={styles.imagePreview}
								/>
							)}
						</Box>
						<Box className={styles.textContainer}>
							<Typography
								component="div"
								className={styles.fileName}
							>
								{name}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								component="div"
							>
								{filesize(size, { standard: 'jedec' })}
							</Typography>
						</Box>
					</>
				)}
			</CardContent>
			{disableDelete ? null : (
				<>
					<CardActions>
						{showConfirmDelete ? (
							<Box className={styles.confirmDeleteButtons}>
								<IconButton
									onClick={() => {
										onConfirmDelete(id);
										setShowConfirmDelete(false);
									}}
								>
									<CheckIcon />
								</IconButton>
								<IconButton
									onClick={() => setShowConfirmDelete(false)}
								>
									<ClearIcon />
								</IconButton>
							</Box>
						) : (
							<IconButton
								onClick={() => setShowConfirmDelete(true)}
							>
								<DeleteIcon />
							</IconButton>
						)}
					</CardActions>
				</>
			)}

			<Modal
				open={openAttachment}
				onClose={handleCloseModal}
				aria-labelledby="attachment-modal-title"
				aria-describedby="attachment-modal-description"
			>
				<Box className={styles.modalBox}>
					{loading && (
						<CircularProgress
							color="primary"
							size={80}
							thickness={4}
						/>
					)}
					{contentType && contentType.includes('image') ? (
						<>
							{error && (
								<Typography
									color={'error'}
									width={'100%'}
									textAlign={'center'}
								>
									Failed to load image.
								</Typography>
							)}
							<img
								src={previewUrl}
								alt=""
								className={styles.modalImage}
								onLoad={handleLoad}
								onError={handleError}
								style={{
									display: loading ? 'none' : 'block',
									objectFit: 'contain',
								}} // Hide image while loading
							/>
						</>
					) : contentType && contentType.includes('video') ? (
						<>
							{error && (
								<Typography
									color={'error'}
									width={'100%'}
									textAlign={'center'}
								>
									Failed to load video.
								</Typography>
							)}
							<video
								src={previewUrl}
								className={styles.modalImage}
								controls
								onLoadedData={handleLoad}
								onError={handleError}
								style={{
									display: loading ? 'none' : 'block',
									objectFit: 'contain',
								}} // Hide video while loading
							/>
						</>
					) : (
						<Typography id="attachment-modal-description">
							No preview available for this file type.
						</Typography>
					)}
				</Box>
			</Modal>
		</Card>
	);
};

export default AttachmentCard;
